import {useCallback} from 'react';
import copy from 'copy-to-clipboard';
import {Locales} from '@netx/core-next/locales/utils/Locales';
import {useSnackbar} from '@netx/core-components/Snackbar';

const UseCopyToClipboardMessage = Locales((i18n) => ({
	ERROR: i18n('i18n.clipboardCopyError'),
	SUCCESS: i18n('i18n.clipboardCopySuccess'),
}));

export type UseCopyToClipboardParams = {
	/** mime-type */
	format?: string;
	onCopy?: (clipboardData: object) => void;

	useSnackbar?: boolean;
	snackbarErrorMessage?: string;
	snackbarSuccessMessage?: string;
};

/**
 * Copy to clipboard hook\
 * NOTE: `handleCopy` can only be called from a user interaction - so no timeouts etc can be at play
 * @param params Options
 * @returns Copy to clipboard function
 * @example
 * ```tsx
 * 	const handleCopy = useCopyToClipboard();
 * 	...
 * 	onClick((event) => {
 * 		handleCopy('foo');
 * 	}),
 * 	...
 * ```
 */
export const useCopyToClipboard = ({
	useSnackbar: _useSnackbar,
	snackbarErrorMessage = UseCopyToClipboardMessage.ERROR,
	snackbarSuccessMessage = UseCopyToClipboardMessage.SUCCESS,
	...options
}: UseCopyToClipboardParams = {}) => {
	const snackbar = useSnackbar();

	const handleCopy = useCallback(
		(text: string, _options?: Pick<UseCopyToClipboardParams, 'format'>) => {
			const result = copy(text, {...options, ..._options});
			if (_useSnackbar) {
				snackbar({
					severity: result ? 'success' : 'error',
					message: result ? snackbarSuccessMessage : snackbarErrorMessage,
				});
			}
			return result;
		},
		[options, _useSnackbar, snackbarErrorMessage, snackbarSuccessMessage],
	);

	return handleCopy;
};

export default useCopyToClipboard;
